/**
 * useSoftphone — initializes the softphone once after authentication.
 *
 * Calls initSoftphone() from client/lib/softphone.ts which handles:
 * - Fetching apex-voice URL from /api/config/voice-url
 * - Connecting voice WebSocket to apex-voice
 * - Configuring Twilio Voice SDK (registers on first user gesture)
 * - Cross-tab call coordination via BroadcastChannel
 *
 * Also starts leader election (client/lib/leader-election.ts) which
 * determines which tab owns the voice WS connection. The election is
 * a standalone module so Vite cannot tree-shake it — this was the root
 * cause of voice WS never connecting in production.
 *
 * Safe to call multiple times — both init functions are idempotent.
 * If voice service is unavailable, the app still works normally.
 */

import { useEffect, useRef } from 'react';
import { initLeaderElection } from '../../lib/leader-election';

export function useSoftphone(isAuthenticated: boolean) {
  const initedRef = useRef(false);

  useEffect(() => {
    if (!isAuthenticated || initedRef.current) return;
    initedRef.current = true;

    // Start leader election before softphone init. Idempotent — safe if
    // vanilla ws-client.ts also calls it (it won't in the React path,
    // but this is defensive).
    initLeaderElection();

    // Dynamic import so the softphone module (and @twilio/voice-sdk)
    // only loads after auth, not on initial page load
    import('../../lib/softphone')
      .then(({ initSoftphone }) => {
        initSoftphone();
      })
      .catch((err) => {
        // Voice service unavailable — not a fatal error
        console.warn('[useSoftphone] Failed to initialize:', err);
      });
  }, [isAuthenticated]);
}
